import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Tabs, TabsList, TabsTrigger } from '../components/ui/tabs';
import { 
  ArrowLeft,
  Plus,
  Search,
  Edit,
  Trash2,
  FileText,
  Stethoscope,
  Scissors,
  AlertTriangle,
  Check,
  X
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SalesCatalogPage = () => {
  const { token, user } = useAuth();
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [typeFilter, setTypeFilter] = useState('');
  const [activeFilter, setActiveFilter] = useState('true');
  
  // Modal state
  const [showModal, setShowModal] = useState(false);
  const [editingService, setEditingService] = useState(null);
  const [formData, setFormData] = useState({
    codigo: '',
    nombre: '',
    tipo: 'CONSULTA',
    categoria: '',
    precio_normal: 0,
    precio_b: 0,
    precio_c: 0,
    descripcion: '',
    requiere_anestesia: false,
    is_active: true
  });
  const [formError, setFormError] = useState('');
  const [saving, setSaving] = useState(false);

  const fetchServices = useCallback(async () => {
    try {
      const params = new URLSearchParams();
      if (typeFilter) params.append('tipo', typeFilter);
      if (activeFilter) params.append('is_active', activeFilter);
      if (searchTerm) params.append('search', searchTerm);
      
      const response = await axios.get(`${API}/sales/catalog?${params.toString()}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setServices(response.data);
    } catch (error) {
      console.error('Error fetching services:', error);
    } finally {
      setLoading(false);
    }
  }, [token, typeFilter, activeFilter, searchTerm]);

  useEffect(() => {
    fetchServices();
  }, [fetchServices]);

  const openNewModal = () => {
    setEditingService(null);
    setFormData({
      codigo: '',
      nombre: '',
      tipo: 'CONSULTA',
      categoria: '',
      precio_normal: 0,
      precio_b: 0,
      precio_c: 0,
      descripcion: '',
      requiere_anestesia: false,
      is_active: true
    });
    setFormError('');
    setShowModal(true);
  };

  const openEditModal = (service) => {
    setEditingService(service);
    setFormData({
      codigo: service.codigo,
      nombre: service.nombre,
      tipo: service.tipo,
      categoria: service.categoria || '',
      precio_normal: service.precio_normal,
      precio_b: service.precio_b,
      precio_c: service.precio_c,
      descripcion: service.descripcion || '',
      requiere_anestesia: service.requiere_anestesia,
      is_active: service.is_active
    });
    setFormError('');
    setShowModal(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.nombre.trim()) {
      setFormError('El nombre es obligatorio');
      return;
    }
    if (formData.precio_normal <= 0) {
      setFormError('El precio normal debe ser mayor a 0');
      return;
    }

    setSaving(true);
    setFormError('');

    try {
      if (editingService) {
        await axios.put(`${API}/sales/catalog/${editingService.id}`, formData, {
          headers: { Authorization: `Bearer ${token}` }
        });
      } else {
        await axios.post(`${API}/sales/catalog`, formData, {
          headers: { Authorization: `Bearer ${token}` }
        });
      }
      setShowModal(false);
      fetchServices();
    } catch (error) {
      setFormError(error.response?.data?.detail || 'Error al guardar');
    } finally {
      setSaving(false);
    }
  };

  const formatCurrency = (amount) => {
    return `Q ${amount?.toLocaleString('es-GT', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) || '0.00'}`;
  };

  const getTypeIcon = (tipo) => {
    switch (tipo) {
      case 'CONSULTA': return <Stethoscope className="h-4 w-4" />;
      case 'CIRUGIA': return <Scissors className="h-4 w-4" />;
      default: return <FileText className="h-4 w-4" />;
    }
  };

  const getTypeBadge = (tipo) => {
    const styles = {
      CONSULTA: 'bg-blue-100 text-blue-700 border-blue-200',
      CIRUGIA: 'bg-purple-100 text-purple-700 border-purple-200',
      PROCEDIMIENTO: 'bg-teal-100 text-teal-700 border-teal-200'
    };
    const labels = {
      CONSULTA: 'Consulta',
      CIRUGIA: 'Cirugía',
      PROCEDIMIENTO: 'Procedimiento'
    };
    return (
      <Badge className={`${styles[tipo] || 'bg-gray-100'} border`}>
        {getTypeIcon(tipo)}
        <span className="ml-1">{labels[tipo] || tipo}</span>
      </Badge>
    );
  };

  const canEdit = user?.role === 'admin' || user?.role === 'superadmin';

  if (!canEdit) {
    return (
      <div className="p-8 text-center">
        <AlertTriangle className="h-12 w-12 mx-auto text-amber-500 mb-4" />
        <h2 className="text-xl font-semibold text-slate-700">Acceso Restringido</h2>
        <p className="text-slate-500 mt-2">Solo administradores pueden gestionar el catálogo.</p>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="sales-catalog-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div className="flex items-center gap-4">
          <Link to="/admin/ventas">
            <Button variant="ghost" size="sm">
              <ArrowLeft className="h-4 w-4" />
            </Button>
          </Link>
          <div>
            <h1 className="text-2xl lg:text-3xl font-bold text-[#0B3B71]" style={{ fontFamily: 'Fraunces, serif' }}>
              Catálogo de Servicios
            </h1>
            <p className="text-slate-500 mt-1">Gestiona consultas, cirugías y procedimientos</p>
          </div>
        </div>
        <Button className="bg-[#0891B2] hover:bg-[#0e7490]" onClick={openNewModal}>
          <Plus className="h-4 w-4 mr-2" />
          Nuevo Servicio
        </Button>
      </div>

      {/* Filters */}
      <Card className="border-0 shadow-sm">
        <CardContent className="p-4">
          <div className="flex flex-col md:flex-row gap-4 items-center">
            <div className="relative flex-1 w-full">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
              <Input
                placeholder="Buscar por código o nombre..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
              />
            </div>
            <select
              value={typeFilter}
              onChange={(e) => setTypeFilter(e.target.value)}
              className="h-10 px-3 rounded-md border border-slate-200 bg-white text-sm"
            >
              <option value="">Todos los tipos</option>
              <option value="CONSULTA">Consultas</option>
              <option value="CIRUGIA">Cirugías</option>
              <option value="PROCEDIMIENTO">Procedimientos</option>
            </select>
            <select
              value={activeFilter}
              onChange={(e) => setActiveFilter(e.target.value)}
              className="h-10 px-3 rounded-md border border-slate-200 bg-white text-sm"
            >
              <option value="true">Activos</option>
              <option value="false">Inactivos</option>
              <option value="">Todos</option>
            </select>
          </div>
        </CardContent>
      </Card>

      {/* Services Table */}
      <Card className="border-0 shadow-sm">
        <CardHeader className="pb-3">
          <CardTitle className="text-lg font-semibold text-[#0B3B71] flex items-center gap-2">
            <FileText className="h-5 w-5" />
            Servicios ({services.length})
          </CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <div className="flex gap-2">
                <span className="w-3 h-3 bg-[#0891B2] rounded-full animate-bounce" style={{ animationDelay: '0ms' }}></span>
                <span className="w-3 h-3 bg-[#0891B2] rounded-full animate-bounce" style={{ animationDelay: '150ms' }}></span>
                <span className="w-3 h-3 bg-[#0891B2] rounded-full animate-bounce" style={{ animationDelay: '300ms' }}></span>
              </div>
            </div>
          ) : services.length === 0 ? (
            <div className="text-center py-12 text-slate-500">
              <FileText className="h-12 w-12 mx-auto mb-3 opacity-30" />
              <p>No hay servicios en el catálogo</p>
              <Button className="mt-4 bg-[#0891B2]" onClick={openNewModal}>
                <Plus className="h-4 w-4 mr-2" />
                Crear primer servicio
              </Button>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-slate-100">
                    <th className="text-left py-3 px-2 text-xs font-medium text-slate-500 uppercase">Código</th>
                    <th className="text-left py-3 px-2 text-xs font-medium text-slate-500 uppercase">Nombre</th>
                    <th className="text-left py-3 px-2 text-xs font-medium text-slate-500 uppercase">Tipo</th>
                    <th className="text-left py-3 px-2 text-xs font-medium text-slate-500 uppercase">Categoría</th>
                    <th className="text-right py-3 px-2 text-xs font-medium text-slate-500 uppercase">P. Normal</th>
                    <th className="text-right py-3 px-2 text-xs font-medium text-slate-500 uppercase">P. B</th>
                    <th className="text-right py-3 px-2 text-xs font-medium text-slate-500 uppercase">P. C</th>
                    <th className="text-center py-3 px-2 text-xs font-medium text-slate-500 uppercase">Estado</th>
                    <th className="text-center py-3 px-2 text-xs font-medium text-slate-500 uppercase">Acciones</th>
                  </tr>
                </thead>
                <tbody>
                  {services.map((service) => (
                    <tr 
                      key={service.id} 
                      className="border-b border-slate-50 hover:bg-slate-50/50 transition-colors"
                    >
                      <td className="py-3 px-2">
                        <span className="font-mono text-sm text-slate-600">{service.codigo}</span>
                      </td>
                      <td className="py-3 px-2">
                        <span className="font-medium text-slate-800">{service.nombre}</span>
                      </td>
                      <td className="py-3 px-2">
                        {getTypeBadge(service.tipo)}
                      </td>
                      <td className="py-3 px-2 text-sm text-slate-600">
                        {service.categoria || '-'}
                      </td>
                      <td className="py-3 px-2 text-right">
                        <span className="font-mono text-sm font-semibold text-slate-800">
                          {formatCurrency(service.precio_normal)}
                        </span>
                      </td>
                      <td className="py-3 px-2 text-right">
                        <span className="font-mono text-sm text-slate-600">
                          {service.precio_b > 0 ? formatCurrency(service.precio_b) : '-'}
                        </span>
                      </td>
                      <td className="py-3 px-2 text-right">
                        <span className="font-mono text-sm text-slate-600">
                          {service.precio_c > 0 ? formatCurrency(service.precio_c) : '-'}
                        </span>
                      </td>
                      <td className="py-3 px-2 text-center">
                        <Badge className={service.is_active ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'}>
                          {service.is_active ? 'Activo' : 'Inactivo'}
                        </Badge>
                      </td>
                      <td className="py-3 px-2 text-center">
                        <Button
                          variant="ghost"
                          size="sm"
                          className="text-slate-600 hover:text-[#0891B2]"
                          onClick={() => openEditModal(service)}
                        >
                          <Edit className="h-4 w-4" />
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Service Modal */}
      <Dialog open={showModal} onOpenChange={setShowModal}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="text-[#0B3B71]">
              {editingService ? 'Editar Servicio' : 'Nuevo Servicio'}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            {formError && (
              <div className="p-3 bg-red-50 border border-red-200 rounded-lg flex items-center gap-2 text-red-700 text-sm">
                <AlertTriangle className="h-4 w-4" />
                {formError}
              </div>
            )}

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Código</Label>
                <Input
                  value={formData.codigo}
                  onChange={(e) => setFormData({ ...formData, codigo: e.target.value.toUpperCase() })}
                  placeholder="SRV-0001 (auto)"
                  className="mt-1"
                />
              </div>
              <div>
                <Label>Tipo *</Label>
                <Select value={formData.tipo} onValueChange={(v) => setFormData({ ...formData, tipo: v })}>
                  <SelectTrigger className="mt-1">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-white">
                    <SelectItem value="CONSULTA">Consulta</SelectItem>
                    <SelectItem value="CIRUGIA">Cirugía</SelectItem>
                    <SelectItem value="PROCEDIMIENTO">Procedimiento</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div>
              <Label>Nombre *</Label>
              <Input
                value={formData.nombre}
                onChange={(e) => setFormData({ ...formData, nombre: e.target.value })}
                placeholder="Ej: Consulta oftalmológica"
                className="mt-1"
              />
            </div>

            <div>
              <Label>Categoría</Label>
              <Input
                value={formData.categoria}
                onChange={(e) => setFormData({ ...formData, categoria: e.target.value })}
                placeholder="Ej: Catarata, Refractiva, Glaucoma"
                className="mt-1"
              />
            </div>

            <div className="grid grid-cols-3 gap-4">
              <div>
                <Label>Precio Normal (Q) *</Label>
                <Input
                  type="number"
                  value={formData.precio_normal}
                  onChange={(e) => setFormData({ ...formData, precio_normal: parseFloat(e.target.value) || 0 })}
                  className="mt-1 font-mono"
                />
              </div>
              <div>
                <Label>Precio B (Q)</Label>
                <Input
                  type="number"
                  value={formData.precio_b}
                  onChange={(e) => setFormData({ ...formData, precio_b: parseFloat(e.target.value) || 0 })}
                  className="mt-1 font-mono"
                />
              </div>
              <div>
                <Label>Precio C (Q)</Label>
                <Input
                  type="number"
                  value={formData.precio_c}
                  onChange={(e) => setFormData({ ...formData, precio_c: parseFloat(e.target.value) || 0 })}
                  className="mt-1 font-mono"
                />
              </div>
            </div>

            <div>
              <Label>Descripción</Label>
              <Input
                value={formData.descripcion}
                onChange={(e) => setFormData({ ...formData, descripcion: e.target.value })}
                placeholder="Descripción opcional..."
                className="mt-1"
              />
            </div>

            {formData.tipo === 'CIRUGIA' && (
              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="requiere_anestesia"
                  checked={formData.requiere_anestesia}
                  onChange={(e) => setFormData({ ...formData, requiere_anestesia: e.target.checked })}
                  className="h-4 w-4 rounded border-slate-300"
                />
                <Label htmlFor="requiere_anestesia" className="cursor-pointer">Requiere anestesia</Label>
              </div>
            )}

            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                id="is_active"
                checked={formData.is_active}
                onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                className="h-4 w-4 rounded border-slate-300"
              />
              <Label htmlFor="is_active" className="cursor-pointer">Activo</Label>
            </div>

            <div className="flex gap-2 pt-4">
              <Button type="button" variant="outline" className="flex-1" onClick={() => setShowModal(false)}>
                Cancelar
              </Button>
              <Button type="submit" className="flex-1 bg-[#0891B2]" disabled={saving}>
                {saving ? 'Guardando...' : (editingService ? 'Actualizar' : 'Crear')}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default SalesCatalogPage;
